export interface ImageFilters {
  brightness: number;
  contrast: number;
  saturation: number;
  rotation: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
}

/**
 * Debounce function to limit the rate of function calls
 * @param func - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const debounce = <T extends (...args: any[]) => any>(func: T, delay: number): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Transform image data at binary level for precision and accuracy
 * @param imageUrl - Source image URL (blob or data URL)
 * @param filters - Image transformation filters to apply
 * @returns Promise resolving to transformed image blob URL
 */
export const transformImageData = async (imageUrl: string, filters: ImageFilters): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // Calculate actual dimensions after rotation
        const radians = (filters.rotation * Math.PI) / 180;
        const cos = Math.abs(Math.cos(radians));
        const sin = Math.abs(Math.sin(radians));

        // Calculate new canvas dimensions to fit rotated image exactly
        const newWidth = Math.ceil(img.width * cos + img.height * sin);
        const newHeight = Math.ceil(img.width * sin + img.height * cos);

        canvas.width = newWidth;
        canvas.height = newHeight;

        // Clear canvas with transparent background
        ctx.clearRect(0, 0, newWidth, newHeight);

        // Move to center for rotation
        ctx.translate(newWidth / 2, newHeight / 2);

        // Apply transformations in correct order
        ctx.rotate(radians);
        if (filters.flipHorizontal) ctx.scale(-1, 1);
        if (filters.flipVertical) ctx.scale(1, -1);

        // Apply filters
        ctx.filter = `brightness(${filters.brightness}%) contrast(${filters.contrast}%) saturate(${filters.saturation}%)`;

        // Draw image centered
        ctx.drawImage(img, -img.width / 2, -img.height / 2, img.width, img.height);

        // Convert to blob URL
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const transformedUrl = URL.createObjectURL(blob);
              resolve(transformedUrl);
            } else {
              reject(new Error('Failed to create blob from transformed image'));
            }
          },
          'image/png',
          1.0,
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
};

/**
 * Check if any image transformations are applied
 * @param filters - Image filters to check
 * @returns boolean indicating if transformations are applied
 */
export const hasImageTransformations = (filters: ImageFilters): boolean => {
  return (
    filters.rotation !== 0 ||
    filters.flipHorizontal ||
    filters.flipVertical ||
    filters.brightness !== 100 ||
    filters.contrast !== 100 ||
    filters.saturation !== 100
  );
};

/**
 * Get default image filters
 * @returns Default ImageFilters object
 */
export const getDefaultImageFilters = (): ImageFilters => ({
  brightness: 100,
  contrast: 100,
  saturation: 100,
  rotation: 0,
  flipHorizontal: false,
  flipVertical: false,
});

/**
 * Safely revoke object URL if it's a blob URL
 * @param url - URL to revoke
 */
export const safeRevokeObjectURL = (url: string | null): void => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

/**
 * Calculate precise screen capture coordinates accounting for video aspect ratio and letterboxing
 * @param selectionRect - Selection rectangle in canvas/overlay coordinates
 * @param video - Video element reference
 * @param canvas - Canvas element used for drawing the selection
 * @returns Precise video coordinates for capture
 */
export const calculatePreciseVideoCoordinates = (
  selectionRect: { x: number; y: number; w: number; h: number },
  video: HTMLVideoElement,
  canvas?: HTMLCanvasElement,
) => {
  // Get actual video stream dimensions
  const videoStreamWidth = video.videoWidth;
  const videoStreamHeight = video.videoHeight;

  // Get video element display dimensions
  const videoDisplayWidth = video.offsetWidth;
  const videoDisplayHeight = video.offsetHeight;

  // Get canvas dimensions (fallback to video display if canvas not provided)
  const canvasWidth = canvas?.width ?? videoDisplayWidth;
  const canvasHeight = canvas?.height ?? videoDisplayHeight;

  console.log('=== COORDINATE CALCULATION DEBUG ===');
  console.log('Video stream:', videoStreamWidth, 'x', videoStreamHeight);
  console.log('Video display:', videoDisplayWidth, 'x', videoDisplayHeight);
  console.log('Canvas:', canvasWidth, 'x', canvasHeight);
  console.log('Selection rect:', selectionRect);

  // Calculate video aspect ratio
  const videoAspectRatio = videoStreamWidth / videoStreamHeight;
  const displayAspectRatio = videoDisplayWidth / videoDisplayHeight;

  // Determine if video is letterboxed (black bars on top/bottom) or pillarboxed (black bars on sides)
  let actualVideoWidth: number;
  let actualVideoHeight: number;
  let videoOffsetX = 0;
  let videoOffsetY = 0;

  if (videoAspectRatio > displayAspectRatio) {
    // Video is wider than display area - pillarboxed (black bars on sides)
    actualVideoWidth = videoDisplayWidth;
    actualVideoHeight = videoDisplayWidth / videoAspectRatio;
    videoOffsetY = (videoDisplayHeight - actualVideoHeight) / 2;
  } else {
    // Video is taller than display area - letterboxed (black bars on top/bottom)
    actualVideoHeight = videoDisplayHeight;
    actualVideoWidth = videoDisplayHeight * videoAspectRatio;
    videoOffsetX = (videoDisplayWidth - actualVideoWidth) / 2;
  }

  console.log('Actual video display area:', actualVideoWidth, 'x', actualVideoHeight);
  console.log('Video offset:', videoOffsetX, 'x', videoOffsetY);

  // Convert canvas coordinates to video display coordinates
  const canvasToVideoScaleX = videoDisplayWidth / canvasWidth;
  const canvasToVideoScaleY = videoDisplayHeight / canvasHeight;

  const videoDisplayX = selectionRect.x * canvasToVideoScaleX;
  const videoDisplayY = selectionRect.y * canvasToVideoScaleY;
  const videoDisplayW = selectionRect.w * canvasToVideoScaleX;
  const videoDisplayH = selectionRect.h * canvasToVideoScaleY;

  console.log('Video display coords:', videoDisplayX, videoDisplayY, videoDisplayW, videoDisplayH);

  // Adjust for letterboxing/pillarboxing
  const adjustedX = videoDisplayX - videoOffsetX;
  const adjustedY = videoDisplayY - videoOffsetY;

  console.log('Adjusted coords (removing letterbox/pillarbox):', adjustedX, adjustedY);

  // Check if selection is within actual video area
  if (adjustedX < 0 || adjustedY < 0 || adjustedX + videoDisplayW > actualVideoWidth || adjustedY + videoDisplayH > actualVideoHeight) {
    console.warn('Selection extends outside video content area');
  }

  // Convert to video stream coordinates
  const streamScaleX = videoStreamWidth / actualVideoWidth;
  const streamScaleY = videoStreamHeight / actualVideoHeight;

  const streamX = adjustedX * streamScaleX;
  const streamY = adjustedY * streamScaleY;
  const streamW = videoDisplayW * streamScaleX;
  const streamH = videoDisplayH * streamScaleY;

  console.log('Final stream coordinates:', streamX, streamY, streamW, streamH);

  // Apply bounds checking to prevent invalid coordinates
  const boundedX = Math.max(0, Math.min(streamX, videoStreamWidth - 1));
  const boundedY = Math.max(0, Math.min(streamY, videoStreamHeight - 1));

  // Calculate available space from the bounded start position
  const maxAvailableWidth = videoStreamWidth - boundedX;
  const maxAvailableHeight = videoStreamHeight - boundedY;

  // Use the smaller of requested size or available space
  const boundedW = Math.min(streamW, maxAvailableWidth);
  const boundedH = Math.min(streamH, maxAvailableHeight);

  console.log('Bounded final coordinates:', boundedX, boundedY, boundedW, boundedH);
  console.log('=== END COORDINATE CALCULATION DEBUG ===');

  return {
    sourceX: Math.floor(boundedX),
    sourceY: Math.floor(boundedY),
    sourceWidth: Math.ceil(boundedW),
    sourceHeight: Math.ceil(boundedH),
    scaleX: streamScaleX,
    scaleY: streamScaleY,
    videoContentArea: {
      x: videoOffsetX,
      y: videoOffsetY,
      width: actualVideoWidth,
      height: actualVideoHeight,
    },
  };
};
